import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Countdown } from '@liveauctioneers/hammer-ui-core/countdown';
import { useFeatureFlags } from '@liveauctioneers/hammer-ui-core/featureFlags/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { ReactNode, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { BidConsoleOpener } from '@/components/BidConsoleOpener/BidConsoleOpener';
import { CatalogRegistrationMessagesExperiment } from '@/components/CatalogBannerExperiment/BannerActions/CatalogRegistrationMessagesExperiment';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { EventContext } from '@/redux/modules/analytics';
import { loadPageData } from '@/redux/modules/appState';
import {
    getCatalogIsCatalogOnly,
    getCatalogIsInstantAutopay,
    getCatalogStartTime,
    getCatalogStatus,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getIsRegistered,
    getRegistrationSuspended,
} from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { CompleteAccountModalContext, ModalContext, openRegisterForAuctionModal } from '@/redux/modules/modal';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import { useTrackRegisterForAuctionPressedAnalytics } from '@/utils/amplitude/analytics';
import { useAmplitudeExperiments } from '@/utils/amplitude/experiments/utils/useAmplitudeExperiments';
import { shouldShowLive } from '@/utils/live';

type Props = {
    catalogId: number;
    className?: string;
    forceIsLive: boolean;
    setForceIsLive: (force: boolean) => void;
};

// Mobile/inline-layout counterpart to TimedOrLiveAuctionStatus, split out into its own component
// (rather than branching the sidebar version on a `layout` prop) so the Figma mobile CTA row can
// evolve independently of the desktop sidebar without conditionals threaded through shared JSX.
const TimedOrLiveAuctionStatusMobile = ({ catalogId, className, forceIsLive, setForceIsLive }: Props) => {
    const { variants } = useAmplitudeExperiments();
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const featureFlags = useFeatureFlags();

    const reloadPageData = useCallback(
        () => dispatch(loadPageData({ amplitudeExperimentFlags: variants, featureFlags, location })),
        [dispatch, location, variants, featureFlags]
    );
    const isWhiteLabel = useAppSelector(getIsWhiteLabel);
    const saleStartTs = useAppSelector((state) => getCatalogStartTime(state, catalogId));
    const registered = useAppSelector((state) => getIsRegistered(state, catalogId));
    const suspended = useAppSelector((state) => getRegistrationSuspended(state, catalogId));
    const catalogStatus = useAppSelector((state) => getCatalogStatus(state, catalogId));
    const isCatalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));
    const isInstantAutopay = useAppSelector((state) => getCatalogIsInstantAutopay(state, catalogId));

    const isLive = forceIsLive ? true : shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);

    const modalContext = useMemo<ModalContext>(
        () => ({
            catalogId,
            eventContext: EventContext.registerForAuction,
            id: catalogId,
            type: CompleteAccountModalContext.RegisterForAuction,
        }),
        [catalogId]
    );

    const trackRegisterForAuctionPressedAnalytics = useTrackRegisterForAuctionPressedAnalytics();

    const registerForCatalog = useCallback(() => {
        trackRegisterForAuctionPressedAnalytics();
        dispatch(
            openRegisterForAuctionModal({
                catalogId,
                fromBiddingConsole: false,
                isInstantAutopay,
                modalContext,
            })
        );
    }, [catalogId, dispatch, isInstantAutopay, modalContext, trackRegisterForAuctionPressedAnalytics]);

    let ctaRowContent: ReactNode;
    if (isLive) {
        ctaRowContent = (
            <BidConsoleOpener
                catalogId={catalogId}
                shouldTrackEnterLiveAuction
            >
                <Button
                    className="mb-0 min-w-0 flex-1"
                    data-testid="catalogEnterLiveAuctionButton"
                    style={isWhiteLabel ? 'primary' : 'critical'}
                    variant="filled"
                >
                    {formatMessage({ id: 'enter_live_auction_mobile' })}
                </Button>
            </BidConsoleOpener>
        );
    } else {
        // Figma's mobile card has no visible countdown here, but the Countdown's
        // onComplete is what flips the page to "live" and reloads data once the
        // sale start passes — stay mounted (just hidden) instead of unmounting, or
        // a mobile visitor sitting on the page never sees it go live without a
        // manual refresh.
        ctaRowContent = (
            <div
                data-testid="countdown"
                hidden
            >
                <Countdown
                    completionTime={saleStartTs * 1000}
                    forceAllUnits
                    onComplete={() => {
                        setForceIsLive(true);
                        reloadPageData();
                    }}
                    small
                    timeLabels={{ days: ' days', hours: ' hrs', minutes: ' mins', seconds: ' secs' }}
                />
            </div>
        );
    }

    return (
        <div
            className={cn('flex h-full max-w-full flex-col items-center justify-start gap-xs', className)}
            data-testid="timed-or-live-auction-status"
        >
            <div
                className={cn('w-full', isLive && 'flex flex-row items-stretch gap-sm')}
                data-testid="timed-or-live-cta-row"
            >
                {ctaRowContent}
                {suspended ? (
                    <Button
                        className={cn(isLive ? 'min-w-0 flex-1' : 'w-full', 'mb-0')}
                        data-testid="catalogAccountSuspendedButton"
                        disabled
                        variant="filled"
                    >
                        {formatMessage({ id: 'account_suspended_mobile' })}
                    </Button>
                ) : (
                    !registered && (
                        <Button
                            className={cn(isLive ? 'min-w-0 flex-1' : 'w-full', 'mb-0')}
                            data-testid="liveCatalogRegisterButton"
                            onClick={registerForCatalog}
                            style={isLive ? 'critical' : 'primary'}
                            variant={isLive ? 'outlined' : 'filled'}
                        >
                            {formatMessage({ id: isLive ? 'register_for_auction_mobile' : 'register_for_auction' })}
                        </Button>
                    )
                )}
            </div>
            {registered && (
                <CatalogRegistrationMessagesExperiment
                    catalogId={catalogId}
                    className="w-full"
                />
            )}
        </div>
    );
};

export default TimedOrLiveAuctionStatusMobile;
